import React, { useRef, useState } from "react";
import "./ImportPopUp.css";
import { Button, Tag, notification } from "antd";
import { FileSearchOutlined } from "@ant-design/icons";
import { BASE_URL } from "../conf/config";
import { Modal } from "antd";
import ImportConfirmPop from "./ImportConfirmPop";
import DocumentImportPop from "./DocumentImportPop";
import { parseWarningsAndErrors } from "../conf/config";
import { useUser } from "../UserContext";

// component for import a single order
const ImportPopUp = ({ isModalOpen, setIsModalOpen }) => {

    const { addNewLog, fetchWithAuth } = useUser();
    const [textareaValue, setTextareaValue] = useState("");
    // a customer's order as PDF or Excel, read by the AI (backend order_import.py)
    const fileInput = useRef(null);
    const [reading, setReading] = useState(false);
    const [documentResult, setDocumentResult] = useState(null);

    // the server reads in the background (a scan takes a while): start it, then ask until it's done
    const waitFor = async (job) => {
        for (let i = 0; i < 150; i++) { // up to 5 minutes
            await new Promise((resolve) => setTimeout(resolve, 2000));
            const response = await fetchWithAuth(`${BASE_URL}/api/order-import/${job}/`);
            const data = await response.json().catch(() => ({}));
            if (!response.ok) return { status: "failed", errors: data.errors || ["The reading was lost. Please try again."] };
            if (data.status !== "running") return data;
        }
        return { status: "failed", errors: ["Reading took too long. Please try again."] };
    };

    const readDocument = async (file) => {
        if (!file) return;
        setReading(true);
        const form = new FormData();
        form.append("file", file);
        const fail = (description) => notification.error({ message: "Could not read the purchase order", description });
        try {
            const response = await fetchWithAuth(`${BASE_URL}/api/order-import/`, { method: "POST", body: form });
            const data = await response.json().catch(() => ({}));
            if (response.status === 413) {
                fail("The file is larger than the server accepts.");
            } else if (!response.ok) {
                fail(data.errors?.[0] || "Please try again.");
            } else {
                const job = await waitFor(data.job);
                if (job.status === "done") setDocumentResult({ ...job.result, job: data.job }); // the job, for corrections and feedback
                else fail(job.errors?.[0] || "Please try again.");
            }
        } catch (e) {
            fail("The server could not be reached.");
        }
        setReading(false);
        if (fileInput.current) fileInput.current.value = "";
    };

    // confirmation pop up before the import
    const [confirmation, setConfirmation] = useState(false);
    const [response, setResponse] = useState([]);
    const [errorMsg, setErrorMsg] = useState([]);
    const [fields, setFields] = useState([]);

    const handleImport = () => {
        const msg = { type: "Import", data: textareaValue };
        document.querySelectorAll('.confirm-button').forEach(button => {
            button.disabled = true;
        });

        if (!checkImport(textareaValue)) {
            notification.warning({
                message: 'Warning',
                description: 'Required fields are missing, Please check your input.',
            });
            return;
        }

        fetchWithAuth(`${BASE_URL}/api/orders/`, {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                // 'Authorization': `Bearer ${localStorage.getItem('access_token')}`,
            },
            body: JSON.stringify(msg),
        })
            .then(res => res.json())
            .then(data => {

                const { parsedWarnings, parsedErrors } = parseWarningsAndErrors(data);
                addNewLog("import orders", parsedWarnings, parsedErrors, msg);
                console.log("parsedErrors.length", parsedErrors.length);

                if (parsedWarnings.length > 0) {
                    notification.warning({
                        message: 'Warning',
                        description: "Some warnings occurred.", parsedWarnings,
                    });
                }

                if (parsedErrors.length > 0) {
                    notification.error({
                        message: 'Error',
                        description: "An error occurred.", parsedErrors,
                    });
                } else {
                    console.log(data);

                    document.querySelectorAll('.confirm-button').forEach(button => {
                        button.disabled = false;
                    });


                    const firstLine = textareaValue.split("\n")[0];
                    const fieldsList = firstLine.split(/;|\t/);
                    setFields(fieldsList);
                    const requiredFields = ["position_ID", "order_ID", "CPN"];
                    const missingFields = requiredFields.some(field => !fieldsList.includes(field));

                    if (missingFields) {
                        notification.error({
                            message: 'Error',
                            description: 'Invalid fields detected, please check your input.',
                        })
                    }

                    setConfirmation(true);
                    setResponse(data);
                    setTextareaValue(""); // Clear the textarea after successful import

                }
            })
            .catch(error => {
                console.error('Error:', error);
                notification.error({
                    message: 'Error',
                    description: 'Failed to import data. Please try again later.',
                });
            });
    };

    const handleClose = () => {
        setIsModalOpen(false);
    };

    function checkImport(input) {
        const requiredFields = ["position_ID", "order_ID", "CPN", "shipping_date_planned", "quantity", "order_type", "customer"];
        for (let i = 0; i < requiredFields.length; i++) {
            if (!input.includes(requiredFields[i])) {
                return false;
            }
        }
        return true;
    }

    return (
        <Modal
            open={isModalOpen}
            footer={null}
            title={null}
            closable={false}
            style={{
                top: '20vh',
                height: '50vh',
            }}
            width={"80vw"}>
            <div className="import-popup__content">
                <Button className="import-popup__close-button" type="primary" danger onClick={handleClose}>close</Button>
                <h2>Import Orders</h2>
                <div className="import-popup__document" data-help-id="import-document">
                    <b>Purchase order from a document</b> <Tag color="purple">AI</Tag>
                    <div>
                        A customer's purchase order (PO), new or revised, as PDF (also scanned) or Excel. The AI reads it;
                        you check every position before anything is saved.
                    </div>
                    <div className="import-popup__note">
                        Purchase orders only: delivery schedules, releases and forecasts (SAs) are not supported.
                    </div>
                    <div style={{ marginTop: 8 }}>
                        <input ref={fileInput} type="file" accept=".pdf,.xlsx,.xlsm" style={{ display: "none" }}
                            onChange={(e) => readDocument(e.target.files[0])} />
                        <Button icon={<FileSearchOutlined />} loading={reading} onClick={() => fileInput.current?.click()}>
                            {reading ? "Reading… (a scan takes up to a minute)" : "Read a purchase order (AI)"}
                        </Button>
                    </div>
                </div>
                <h3 style={{ marginTop: 18 }}>Or paste a table</h3>
                <div><b>required fields:</b> position_ID;order_ID;CPN;shipping_date_planned;quantity;order_type;customer</div>
                <div className="import-popup__textarea-container">
                    <textarea
                        className="import-popup__textarea"
                        value={textareaValue}
                        onChange={(e) => setTextareaValue(e.target.value)}
                    ></textarea>
                </div>
                <button className="import-popup__button" onClick={handleImport}>Import</button>
            </div>
            <ImportConfirmPop
                confirmation={confirmation}
                setConfirmation={setConfirmation}
                response={response}
                errorMsg={errorMsg}
                fields={fields}
            />
            <DocumentImportPop result={documentResult} onClose={() => setDocumentResult(null)} />
        </Modal >
    );
}

export default ImportPopUp;
